import * as React from "react";
import "./PaymentCtl.scss";
import MonthCtl from "./input/MonthCtl";
import LabelCtl from "./input/LabelCtl";
import TextCtl from "./input/TextCtl";
import SelectCtl from "./input/SelectCtl";
import CurrencyCtl from "./input/CurrencyCtl";
import { Guid } from "@testout/testout-commerce/core/Guid";
import { PaymentTerms, PaymentMethod, CardBrand, Payment } from "@testout/testout-commerce/models/commerce/Payment";
import { ListItem } from "@testout/testout-commerce/models/ListItem";
import PlainDate from "@testout/testout-commerce/models/PlainDate";

interface IPaymentCtlProps {
    paymentTerms: string;
    payment?: Payment;
    purchaseOrderNumber?: string;
    disabled?: boolean;
    isQuote?: boolean;
    onChange?: (paymentTerms: string, payment: Payment | undefined) => void;
    onPurchaseOrderNumberChange?: (value: string) => void;
}

interface IPaymentCtlState {
    text: string;
}

export default class PaymentCtl extends React.PureComponent<IPaymentCtlProps, IPaymentCtlState> {
    constructor(props: IPaymentCtlProps) {
        super(props);
        this.state = { text: "" };
    }

    componentDidUpdate() {
        // Get the string version of the date if we have an existing value
        if (this.state.text === "" && this.props.payment?.cardExpirationDate)
            this.setState({ text: PlainDate.format(this.props.payment.cardExpirationDate, "MM/yy") }) ;
    }

    _paymentTypes: ListItem[] = [
        { name: PaymentMethod[PaymentMethod.Cash] },
        { name: PaymentMethod[PaymentMethod.Check] },
        { name: PaymentMethod[PaymentMethod.Card] + ": Visa", value: CardBrand.Visa.toString() },
        { name: PaymentMethod[PaymentMethod.Card] + ": MasterCard", value: CardBrand.MasterCard.toString() },
        { name: PaymentMethod[PaymentMethod.Card] + ": American Express", value: CardBrand.AmericanExpress.toString() },
        { name: PaymentMethod[PaymentMethod.Card] + ": Discover", value: CardBrand.Discover.toString() },
        { name: PaymentMethod[PaymentMethod.Card] + ": PayPal", value: CardBrand.PayPal.toString() },
        { name: PaymentTerms.Net15.toString() },
        { name: PaymentTerms.Net30.toString() },
        { name: PaymentTerms.Disc2in10Net30.toString() },
        { name: PaymentTerms.Net60.toString() },
        { name: PaymentTerms.Disc2in10Net60.toString() }
    ];

    _quotePaymentTypes: ListItem[] = [
        { name: PaymentTerms.Net15.toString() },
        { name: PaymentTerms.Net30.toString() },
        { name: PaymentTerms.Disc2in10Net30.toString() },
        { name: PaymentTerms.Net60.toString() },
        { name: PaymentTerms.Disc2in10Net60.toString() }
    ];

    getPaymentType(): string {
        if (this.props.paymentTerms === PaymentTerms.Cash.toString()) {
            switch (this.props.payment?.method) {
            case PaymentMethod.Card:
                return this.props.payment.cardBrand || CardBrand.Visa.toString();
            case PaymentMethod.Check:
                return PaymentMethod[PaymentMethod.Check];
            default: //PaymentMethod.Cash or null
                return PaymentMethod[PaymentMethod.Cash];
            }
        } else
            return this.props.paymentTerms;
    }

    changePaymentType = (value: string) => {
        let paymentTerms = PaymentTerms.Cash.toString();
        let payment: Payment | undefined;
        payment = new Payment(this.props.payment);
        if (payment && !payment.paymentId)
            payment.paymentId = Guid.create().toString();
        switch (value) {
            case PaymentMethod[PaymentMethod.Cash]:
                payment.method = PaymentMethod.Cash;
                break;
            case PaymentMethod[PaymentMethod.Check]:
                payment.method = PaymentMethod.Check;
                break;
            default:
                // ReSharper disable once TsResolvedFromInaccessibleModule
                if (Object.values(CardBrand).some(v => v === value)) {
                    payment.method = PaymentMethod.Card;
                    payment.cardBrand = value;
                } else {
                    paymentTerms = value;
                    payment = undefined;
                }
                break;
        }
        this.props.onChange?.(paymentTerms, payment);
    }

    changeAmount = (value: number) => {
        let payment = new Payment(this.props.payment);
        payment.amount = value;
        this.props.onChange?.(this.props.paymentTerms, payment);
    }
    changeCheckNumber = (value: string) => {
        let payment = new Payment(this.props.payment);
        payment.checkNumber = value;
        this.props.onChange?.(this.props.paymentTerms, payment);
    }
    changeCardSuffix = (value: string) => {
        let payment = new Payment(this.props.payment);
        payment.cardSuffix = value;
        this.props.onChange?.(this.props.paymentTerms, payment);
    }
    changeCardExpiration = (value?: Date) => {
        let payment = new Payment(this.props.payment);
        payment.cardExpirationDate = value;
        this.props.onChange?.(this.props.paymentTerms, payment);
    }
    changeCardTransactionCode = (value: string) => {
        let payment = new Payment(this.props.payment);
        payment.cardTransactionCode = value;
        this.props.onChange?.(this.props.paymentTerms, payment);
    }
    expDateChange = (value: string) => {
        //console.log(`expChange(value = ${value}, state.text = ${this.state?.text})`)
        this.setState({ text: value });
    }

    render() {
        const { paymentTerms, payment, purchaseOrderNumber, onPurchaseOrderNumberChange, disabled } = this.props;
        return <div className="payment">
            <div>
                <LabelCtl label="Payment Terms">
                    <SelectCtl value={this.getPaymentType()} disabled={disabled} onChange={this.changePaymentType} items={this.props.isQuote ? this._quotePaymentTypes : this._paymentTypes} hideItemValue={true} />
                </LabelCtl>
                {paymentTerms === PaymentTerms.Cash.toString() &&
                    <LabelCtl label="Amount"><CurrencyCtl value={payment?.amount ?? 0} onChange={this.changeAmount} disabled={disabled} /></LabelCtl>}
                {paymentTerms !== PaymentTerms.Cash.toString() && !!onPurchaseOrderNumberChange &&
                    <LabelCtl label="PO Number"><TextCtl value={purchaseOrderNumber} disabled={disabled} onChange={onPurchaseOrderNumberChange} /></LabelCtl>}
            </div>
            {paymentTerms === PaymentTerms.Cash.toString() && <div>
                {payment?.method === PaymentMethod.Check &&
                    <LabelCtl label="Check Number"><TextCtl value={payment?.checkNumber}
                        onChange={this.changeCheckNumber} disabled={disabled} /></LabelCtl>}
                {payment?.method === PaymentMethod.Card && <>
                    {payment?.cardBrand !== CardBrand.PayPal.toString() && <>
                        <LabelCtl label="Card Last-4"><TextCtl maxLength={4} value={payment?.cardSuffix} disabled={disabled}
                            onChange={this.changeCardSuffix} /></LabelCtl>
                        <LabelCtl label="Expiration"><MonthCtl placeholder="MM/DD" text={this.state.text} onTextChange={this.expDateChange} disabled={disabled}
                            value={payment?.cardExpirationDate} onChange={this.changeCardExpiration} /></LabelCtl>
                    </>}
                    <LabelCtl label="Transaction Number"><TextCtl value={payment?.cardTransactionCode} disabled={disabled}
                        onChange={this.changeCardTransactionCode} /></LabelCtl>
                </>}
            </div>}
        </div>;
    }
}